import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import ApiService from '../../services/api';

interface Service {
  id: number;
  name: string;
  description: string;
  price: number;
  duration: number;
  category: string;
  image?: string;
  features?: string[];
  isActive: boolean;
  isPopular: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  id: number;
  name: string;
  displayName: string;
  icon: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: '',
    image: '',
    features: [] as string[],
    displayOrder: 0
  });

  // Kategorileri yükle
  const loadCategories = async () => {
    try {
      const response = await ApiService.getAdminCategories();
      const categoriesData: Category[] = response.categories || [];
      setCategories(categoriesData);
      
      // İlk aktif kategoriyi varsayılan olarak ayarla
      const firstActiveCategory = categoriesData
        .filter((cat: Category) => cat.isActive)
        .sort((a: Category, b: Category) => a.displayOrder - b.displayOrder)[0];
      
      if (firstActiveCategory && !formData.category) {
        setFormData(prev => ({ ...prev, category: firstActiveCategory.name }));
      }
    } catch (error: any) {
      console.error('Kategoriler yüklenirken hata:', error);
      setError('Kategoriler yüklenemedi: ' + error.message);
    }
  };

  // Hizmetleri yükle
  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminServices();
      setServices(response.services);
    } catch (error: any) {
      console.error('Hizmetler yüklenirken hata:', error);
      setError('Hizmetler yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadServices();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  // Form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const serviceData = {
        ...formData,
        price: parseFloat(formData.price),
        duration: parseInt(formData.duration)
      };

      if (editingService) {
        await ApiService.updateService(editingService.id, serviceData);
      } else {
        await ApiService.createService(serviceData);
      }

      setShowModal(false);
      setEditingService(null);
      // İlk aktif kategoriyi varsayılan olarak ayarla
      const firstActiveCategory = categories
        .filter((cat: Category) => cat.isActive)
        .sort((a: Category, b: Category) => a.displayOrder - b.displayOrder)[0];
      
      setFormData({
        name: '',
        description: '',
        price: '',
        duration: '',
        category: firstActiveCategory ? firstActiveCategory.name : '',
        image: '',
        features: [],
        displayOrder: 0
      });
      await loadServices();
    } catch (error: any) {
      console.error('Hizmet kaydedilirken hata:', error);
      setError('Hizmet kaydedilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Hizmet düzenle
  const handleEdit = (service: Service) => {
    setEditingService(service);
    
    // Features'ı güvenli şekilde array'e çevir
    let features = [];
    if (service.features) {
      if (Array.isArray(service.features)) {
        features = service.features;
      } else if (typeof service.features === 'string') {
        try {
          features = JSON.parse(service.features);
        } catch (e) {
          console.warn('Features string parse edilemedi:', service.features);
          features = [];
        }
      }
    }
    
    setFormData({
      name: service.name,
      description: service.description,
      price: service.price.toString(),
      duration: service.duration.toString(),
      category: service.category,
      image: service.image || '',
      features: features,
      displayOrder: service.displayOrder || 0
    });
    setShowModal(true);
  };

  // Hizmet sil
  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteService(id);
      await loadServices();
    } catch (error: any) {
      console.error('Hizmet silinirken hata:', error);
      setError('Hizmet silinemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Hizmet durumunu değiştir
  const handleToggleStatus = async (id: number) => {
    try {
      setLoading(true);
      await ApiService.toggleServiceStatus(id);
      await loadServices();
    } catch (error: any) {
      console.error('Hizmet durumu değiştirilirken hata:', error);
      setError('Hizmet durumu değiştirilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Popüler hizmet durumunu değiştir
  const handleTogglePopular = async (id: number) => {
    try {
      setLoading(true);
      await ApiService.toggleServicePopular(id);
      await loadServices();
    } catch (error: any) {
      console.error('Popüler hizmet durumu değiştirilirken hata:', error);
      setError('Popüler hizmet durumu değiştirilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.name === category);
    return cat ? cat.displayName : category;
  };

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Hizmet Yönetimi</h2>
        <button
          onClick={() => {
            setEditingService(null);
            // İlk aktif kategoriyi varsayılan olarak ayarla
            const firstActiveCategory = categories
              .filter((cat: Category) => cat.isActive)
              .sort((a: Category, b: Category) => a.displayOrder - b.displayOrder)[0];
            
            setFormData({
              name: '',
              description: '',
              price: '',
              duration: '',
              category: firstActiveCategory ? firstActiveCategory.name : '',
              image: '',
              features: [],
              displayOrder: 0
            });
            setShowModal(true);
          }}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Hizmet
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Hizmetler Tablosu */}
              <div className="rounded-lg shadow-sm overflow-hidden border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-700">
            <thead style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hizmet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fiyat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Süre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Popüler
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sıra
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
              {services.map((service) => (
                                  <tr key={service.id} className="hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-white">{service.name}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{service.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {getCategoryLabel(service.category)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    ₺{service.price}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {service.duration} dk
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(service.id)}
                      className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                        service.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } transition-colors`}
                    >
                      {service.isActive ? (
                        <><Eye className="w-3 h-3" /> Aktif</>
                      ) : (
                        <><EyeOff className="w-3 h-3" /> Pasif</>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleTogglePopular(service.id)}
                      className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                        service.isPopular
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      <Star className={`w-3 h-3 ${service.isPopular ? 'fill-current' : ''}`} />
                      {service.isPopular ? 'Popüler' : 'Normal'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {service.displayOrder || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(service)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-full max-w-4xl mx-4 text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <h3 className="text-lg font-semibold text-white mb-4">
              {editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet Ekle'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Hizmet Adı
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Kategori
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  >
                    <option value="">Kategori Seçin</option>
                    {categories
                      .filter(cat => cat.isActive)
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.displayName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fiyat (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Süre (dk)
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Emoji/İkon
                  </label>
                  <input
                    type="text"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="💅"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Hizmet Özellikleri
                </label>
                                 <div className="space-y-2">
                   {(Array.isArray(formData.features) ? formData.features : []).map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                                                 onChange={(e) => {
                           const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
                           const newFeatures = [...currentFeatures];
                           newFeatures[index] = e.target.value;
                           setFormData({ ...formData, features: newFeatures });
                         }}
                        placeholder="Özellik açıklaması"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-white"
                        style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                      />
                      <button
                        type="button"
                                                 onClick={() => {
                           const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
                           const newFeatures = currentFeatures.filter((_, i) => i !== index);
                           setFormData({ ...formData, features: newFeatures });
                         }}
                        className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                                         onClick={() => {
                       const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
                       setFormData({ ...formData, features: [...currentFeatures, ''] });
                     }}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-pink-300 hover:text-pink-500 transition-colors"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  >
                    + Özellik Ekle
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Bu özellikler Services sayfasında nokta maddeler olarak gösterilecek
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (editingService ? 'Güncelle' : 'Ekle')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;